import { translationKey } from '../../i18n';
import { localize } from '../../i18n';
import SingleLineText from '../../components/ui/SingleLineText';
import Button from '../../components/ui/FitButton';
import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BottomSheet from '../../components/BottomSheet';
import { checkpointPermissionDraft } from '../../utils/permissionDraft';

export const hiddenPhotoInputStyle = {
    display: 'none',
};

const labelPhotoInputStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
};

export default function MedicationPhotoSheet({
    open,
    onClose,
    onSelectFile,
    onPickCamera,
    onPickLibrary,
    cameraInputRef: cameraInputRefProp,
    libraryInputRef: libraryInputRefProp,
    title = translationKey('약 사진 추가'),
    subtitle = translationKey('선택 사항이에요. 약을 알아보기 쉽게 도와줘요.'),
    cameraDisabled = false,
    libraryDisabled = false,
    disabledReason,
    onDelete,
    deleteDisabled = false,
    singlePickerMode = false,
    singlePickerLabel,
    analysisConsent = false,
}) {
    const internalCameraInputRef = useRef(null);
    const internalLibraryInputRef = useRef(null);
    const pickerOpeningRef = useRef(false);
    const cameraInputRef = cameraInputRefProp || internalCameraInputRef;
    const libraryInputRef = libraryInputRefProp || internalLibraryInputRef;
    const useInternalCameraInput = !cameraInputRefProp && !onPickCamera;
    const useInternalLibraryInput = !libraryInputRefProp && !onPickLibrary;

    const handleChange = (event) => {
        onSelectFile?.(event);
        onClose?.();
    };

    const openPicker = (ref, fallback) => {
        if (pickerOpeningRef.current) return;
        pickerOpeningRef.current = true;
        window.setTimeout(() => {
            pickerOpeningRef.current = false;
        }, 700);
        if (fallback) {
            fallback();
            return;
        }
        const input = ref.current;
        if (!input) return;
        input.value = '';
        checkpointPermissionDraft();
        // File inputs use click() in the original gesture; some WebKit builds expose a no-op showPicker().
        try {
            input.click();
        } catch (_) {}
    };

    return (
        <BottomSheet open={open} onClose={onClose} maxHeight={analysisConsent ? "80vh" : "42vh"}>
            <Box sx={{ pb: 0.5 }}>
                {!cameraInputRefProp && !useInternalCameraInput && (
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleChange}
                        style={hiddenPhotoInputStyle}
                    />
                )}
                {!libraryInputRefProp && !useInternalLibraryInput && (
                    <input
                        ref={libraryInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleChange}
                        style={hiddenPhotoInputStyle}
                    />
                )}
                <SingleLineText sx={{ fontSize: '1.12rem', fontWeight: 750, color: '#1a1a1a', mb: 0.35 }}>
                    {localize(title)}
                </SingleLineText>
                {subtitle && (
                    <SingleLineText sx={{ fontSize: '0.92rem', fontWeight: 550, color: '#777', lineHeight: 1.45, mb: 1.25, wordBreak: 'keep-all' }}>
                        {localize(subtitle)}
                    </SingleLineText>
                )}
                {analysisConsent && (
                    <Box sx={{ bgcolor: '#f7f7f2', p: 2, borderRadius: 3, mb: 2 }}>
                        <Typography sx={{ fontSize: '0.94rem', color: '#242421', lineHeight: 1.65, wordBreak: 'keep-all' }}>{localize("사진 분석을 위해 ")}<strong>{localize("사진과 인식된 글자")}</strong>{localize("를 ")}<strong>{localize('Google Cloud Vision·OpenAI')}</strong>{localize("에 전송해요.")}</Typography>
                        <Typography sx={{ mt: 1.25, fontSize: '0.88rem', color: '#686861', lineHeight: 1.6, wordBreak: 'keep-all' }}>{localize("이름·생년월일 등 불필요한 개인정보는 사진을 찍거나 선택하기 전에 가려주세요.")}</Typography>
                        <SingleLineText component="a" href="/privacy" sx={{ display: 'inline-block', mt: 1.25, color: '#686861', fontSize: '0.82rem', textUnderlineOffset: '3px' }}>{localize("개인정보 처리방침 보기")}</SingleLineText>
                    </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {singlePickerMode ? (
                        <Button
                            fullWidth
                            component={useInternalLibraryInput ? 'label' : 'button'}
                            startIcon={<PhotoLibraryIcon />}
                            disabled={libraryDisabled}
                            onClick={useInternalLibraryInput ? undefined : () => openPicker(libraryInputRef, onPickLibrary)}
                            sx={{
                                position: 'relative',
                                py: 1.25,
                                borderRadius: 3,
                                bgcolor: '#ffe566',
                                color: '#5a4a00',
                                border: '1px solid #f4ce24',
                                fontWeight: 750,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                                '&:disabled': { bgcolor: '#f3f4f6', color: '#aaa', borderColor: '#e5e7eb' },
                            }}
                        >
                            {useInternalLibraryInput && (
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleChange}
                                    onClick={checkpointPermissionDraft}
                                    style={labelPhotoInputStyle}
                                />
                            )}
                            {analysisConsent ? localize("동의하고 사진 선택") : (singlePickerLabel || localize("사진 변경"))}
                        </Button>
                    ) : (
                        <>
                            <Button
                                fullWidth
                                component={useInternalCameraInput ? 'label' : 'button'}
                                startIcon={<CameraAltIcon />}
                                disabled={cameraDisabled}
                                onClick={useInternalCameraInput ? undefined : () => openPicker(cameraInputRef, onPickCamera)}
                                sx={{
                                    position: 'relative',
                                    py: 1.25,
                                    borderRadius: 3,
                                    bgcolor: '#ffe566',
                                    color: '#5a4a00',
                                    border: '1px solid #f4ce24',
                                    fontWeight: 750,
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                                    '&:disabled': { bgcolor: '#f3f4f6', color: '#aaa', borderColor: '#e5e7eb' },
                                }}
                            >
                                {useInternalCameraInput && (
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleChange}
                                        onClick={checkpointPermissionDraft}
                                        style={labelPhotoInputStyle}
                                    />
                                )}
                                {analysisConsent ? localize("동의하고 사진 찍기") : localize("사진 찍기")}
                            </Button>
                            <Button
                                fullWidth
                                component={useInternalLibraryInput ? 'label' : 'button'}
                                startIcon={<PhotoLibraryIcon />}
                                disabled={libraryDisabled}
                                onClick={useInternalLibraryInput ? undefined : () => openPicker(libraryInputRef, onPickLibrary)}
                                sx={{
                                    position: 'relative',
                                    py: 1.25,
                                    borderRadius: 3,
                                    bgcolor: '#fff',
                                    color: '#5a4a00',
                                    border: '1px solid #e5e7eb',
                                    fontWeight: 750,
                                    '&:disabled': { bgcolor: '#f3f4f6', color: '#aaa', borderColor: '#e5e7eb' },
                                }}
                            >
                                {useInternalLibraryInput && (
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleChange}
                                        onClick={checkpointPermissionDraft}
                                        style={labelPhotoInputStyle}
                                    />
                                )}
                                {analysisConsent ? localize("동의하고 앨범에서 선택") : localize("앨범 선택")}
                            </Button>
                        </>
                    )}
                    {onDelete && (
                        <Button
                            fullWidth
                            startIcon={<DeleteOutlineIcon />}
                            disabled={deleteDisabled}
                            onClick={() => {
                                onClose?.();
                                window.setTimeout(onDelete, 120);
                            }}
                            sx={{
                                py: 1.25,
                                borderRadius: 3,
                                bgcolor: '#fff',
                                color: '#5f6368',
                                border: '1px solid #e5e7eb',
                                fontWeight: 750,
                                '&:disabled': { bgcolor: '#f3f4f6', color: '#aaa', borderColor: '#e5e7eb' },
                            }}
                        >{localize("사진 삭제")}</Button>
                    )}
                </Box>
                {analysisConsent && (
                    <Button fullWidth onClick={onClose} sx={{ mt: 1, minHeight: 44, color: '#686861' }}>{localize("취소")}</Button>
                )}
                {disabledReason && (cameraDisabled || libraryDisabled) && (
                    <Typography sx={{ mt: 1.2, fontSize: '0.86rem', color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>
                        {disabledReason}
                    </Typography>
                )}
            </Box>
        </BottomSheet>
    );
}
